import { IShoppingCartProduct } from '@monofront/types';
import { useTranslations } from 'next-intl';
import ShoppingCartOOSProductRow from '@components/ShoppingCartOOSProducts/ShoppingCartOOSProductRow';
import React, { useEffect, useState } from 'react';
import ModalReplaceOOSProduct from '@components/ShoppingCartOOSProducts/ModalReplaceOOSProduct';
import {
  Carousel,
  CarouselSlide,
} from '@monofront/design-system/components/Carousel';
import { space } from '@monofront/theme';
import { dataLayerTrackEvent } from '@utils/tracking';
import { trackEvent } from '@monofront/tracking';
import { growthBookService } from '@services/businessContainer';
import { OOS_RECOMMENDATIONS_WIDGET_ID } from '@monofront/business/constants';

interface IShoppingCartOOSProductsProps {
  oosProducts: IShoppingCartProduct[];
}

const ShoppingCartOOSProducts: React.FC<IShoppingCartOOSProductsProps> = ({
  oosProducts,
}) => {
  const tCart = useTranslations('Cart');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [shoppingCartProductToUpdate, setShoppingCartProductToUpdate] =
    useState<IShoppingCartProduct | undefined>();

  useEffect(() => {
    if (!oosProducts?.length) return;

    // Track only when products change from empty to having items
    // or when new sku is added
    oosProducts?.forEach((shoppingCartOOSProduct) => {
      if (!shoppingCartOOSProduct.replacementProduct) {
        trackEvent({
          type: 'LFSubstitutionNotFound',
          data: {
            productSku: shoppingCartOOSProduct.sku,
            source: `widget:${growthBookService.getFeatureValueWithoutTracking(
              ...OOS_RECOMMENDATIONS_WIDGET_ID,
            )}`,
          },
        });
      }
    });
  }, [oosProducts?.length, oosProducts?.map((p) => p.sku).join(',')]);

  const onOpenModal = (shoppingCartProductToUpdate: IShoppingCartProduct) => {
    setShoppingCartProductToUpdate(shoppingCartProductToUpdate);
    setIsModalOpen(true);
    dataLayerTrackEvent('substitutionPopin');

    const widgetSource = `widget:${growthBookService.getFeatureValueWithoutTracking(
      ...OOS_RECOMMENDATIONS_WIDGET_ID,
    )}`;

    trackEvent({
      type: 'LFSubstitutionPopin',
      data: {
        productSku: shoppingCartProductToUpdate.sku,
        source:
          shoppingCartProductToUpdate.replacementProduct?.replacementSource ??
          '',
        originalSource: widgetSource,
      },
    });

    if (
      shoppingCartProductToUpdate.replacementProduct?.replacementSource ===
      widgetSource
    ) {
      // track only when the product is found by the widget
      growthBookService.enterExperimentFromFeatureFlag(
        OOS_RECOMMENDATIONS_WIDGET_ID[0],
      );
    } else {
      trackEvent({
        type: 'LFSubstitutionNotFound',
        data: {
          productSku: shoppingCartProductToUpdate.sku,
          source: widgetSource,
        },
      });
    }
  };

  return (
    <>
      {shoppingCartProductToUpdate && isModalOpen && (
        <ModalReplaceOOSProduct
          shoppingCartOOSProduct={shoppingCartProductToUpdate}
          isOpen={isModalOpen}
          externalToggle={() => setIsModalOpen(false)}
        />
      )}
      {oosProducts?.length > 0 && (
        <>
          <div className="oos-products-error">
            <p className="error tw-lf-error-message tw-text-base tw-font-bold">
              {tCart('oosProducts', {
                nb: oosProducts.length,
              })}
            </p>
          </div>
          <div className="oos-products desktop-only">
            {oosProducts.map((shoppingCartOOSProduct: IShoppingCartProduct) => (
              <ShoppingCartOOSProductRow
                key={shoppingCartOOSProduct.objectID}
                shoppingCartOOSProduct={shoppingCartOOSProduct}
                onOpenModal={onOpenModal}
              />
            ))}
          </div>
          <div className="oos-products mobile-only">
            <Carousel
              className="carousel-widgets"
              spaceBetween={space[2]}
              slidesPerView="auto"
              navigation={true}
            >
              {oosProducts.map((shoppingCartProduct: IShoppingCartProduct) => (
                <CarouselSlide key={shoppingCartProduct.objectID}>
                  <ShoppingCartOOSProductRow
                    shoppingCartOOSProduct={shoppingCartProduct}
                    onOpenModal={onOpenModal}
                  />
                </CarouselSlide>
              ))}
            </Carousel>
          </div>
        </>
      )}
      <style jsx>{`
        .oos-products-error {
          margin-top: var(--space-4);
        }

        .oos-products {
          background-color: var(--clr-secondary-4);
          border-radius: var(--rd-3);
          margin: var(--space-4) 0;
        }

        .oos-products :global(.oos-products-container):not(:last-child) {
          border-bottom: 1px solid var(--clr-secondary-3);
        }

        @media (max-width: 800px) {
          .oos-products {
            background-color: inherit;
            border-radius: 0;
            padding: 0 var(--space-4);
          }
        }
      `}</style>
    </>
  );
};

export default ShoppingCartOOSProducts;
